import { useState, useMemo } from "react";
import { CustomerList } from "@/hooks/services/pps/handlers/customers-api";
import { PartList } from "@/hooks/services/pps/handlers/part-api";
import { ProcessList } from "@/hooks/services/pps/handlers/process-api";
import { ShopList } from "./services/pps/handlers/shop-api";
import { MachineList } from "./services/pps/handlers/machines-api";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { useDebounce } from "./use-debounce";
import { TechnicianList } from "./services/pps/handlers/technicians-api";

export const REFERENCE_TABLE = {
  ppsProcess: "ppsProcess",
  ppsCustomer: "ppsCustomer",
  ppsPart: "ppsPart",
  ppsShop: "ppsShop",
  ppsMachine: "ppsMachine",
  ppsTechnician: "ppsTechnician",
};

/**
 *
 * @param referenceTable
 * @param nestedColumnName
 * @returns
 *
 * this hook fetches foreign key data based on the reference table and nested column name.
 * It uses the reference table to determine which API to call and fetches the data accordingly.
 * The fetched data is then transformed into a format suitable for use in a combobox.
 */

export function useForeignKeyData(
  referenceTable: string,
  nestedColumnName: string | string[],
) {
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);

  const searchParam = debouncedSearch || undefined;

  // Call all hooks unconditionally (Rules of Hooks) but React Query will cache and optimize
  const processQuery = useGetData(ProcessList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsProcess,
  });
  const customerQuery = useGetData(CustomerList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsCustomer,
  });
  const partQuery = useGetData(PartList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsPart,
  });
  const shopQuery = useGetData(ShopList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsShop,
  });
  const machineQuery = useGetData(MachineList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsMachine,
  });
  const technicianQuery = useGetData(TechnicianList({ search: searchParam }), {
    enabled: referenceTable === REFERENCE_TABLE.ppsTechnician,
  });

  // Select the appropriate query result based on table reference
  let selectedQuery;
  switch (referenceTable) {
    case REFERENCE_TABLE.ppsProcess:
      selectedQuery = processQuery;
      break;
    case REFERENCE_TABLE.ppsCustomer:
      selectedQuery = customerQuery;
      break;
    case REFERENCE_TABLE.ppsPart:
      selectedQuery = partQuery;
      break;
    case REFERENCE_TABLE.ppsShop:
      selectedQuery = shopQuery;
      break;
    case REFERENCE_TABLE.ppsMachine:
      selectedQuery = machineQuery;
      break;
    case REFERENCE_TABLE.ppsTechnician:
      selectedQuery = technicianQuery;
      break;
    default:
      selectedQuery = { data: null, isLoading: false, error: null };
  }

  const { data, isLoading, error } = selectedQuery;

  // Transform data based on table type - memoize to prevent unnecessary recalculations
  const optionsData = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item: any) => {
      let label = "";

      switch (referenceTable) {
        case REFERENCE_TABLE.ppsProcess:
          label = item.processName || "";
          break;
        case REFERENCE_TABLE.ppsCustomer:
          label = item.customerName || "";
          break;
        case REFERENCE_TABLE.ppsPart:
          label = Array.isArray(nestedColumnName)
            ? nestedColumnName
                .map((column: string) => item[column] || "")
                .filter(Boolean)
                .join(" - ")
            : item[nestedColumnName] || "";
          break;
        case REFERENCE_TABLE.ppsShop:
          label = item.shopName || "";
          break;
        case REFERENCE_TABLE.ppsMachine:
          label = item.name || "";
          break;
        case REFERENCE_TABLE.ppsTechnician:
          label = item.name || "";
          break;
        default:
          label = item.name || item.id || "";
      }

      return {
        value: item.id,
        label: label || `ID: ${item.id}`,
      };
    });
  }, [data?.data, referenceTable, nestedColumnName]);

  return {
    optionsData,
    setSearch,
    isLoading: isLoading || false,
    error,
  };
}
